import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Send } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { sendBulkInvites } from '../../services/api/onboardingService';
import { parseEmails } from './utils/inviteUserUtils';

interface InviteUsersDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    facilityName: string;
    departmentId?: string;
    isOnboarding?: boolean;
    onInvited: () => void;
    licenses?: number;
    usedSlots?: number;
}

export default function InviteUsersDialog({
    open,
    onOpenChange,
    facilityName,
    departmentId,
    isOnboarding,
    onInvited,
    licenses,
    usedSlots,
}: InviteUsersDialogProps) {
    const { toast } = useToast();
    const { t } = useTranslation();
    const [pasteValue, setPasteValue] = useState('');
    const [sending, setSending] = useState(false);

    const parsedEmails = parseEmails(pasteValue);
    const availableSlots =
        licenses && licenses > 0
            ? Math.max(0, licenses - (usedSlots ?? 0))
            : null;
    const emails =
        availableSlots === null
            ? parsedEmails.valid
            : parsedEmails.valid.slice(0, availableSlots);
    const overflowCount =
        availableSlots === null
            ? 0
            : Math.max(0, parsedEmails.valid.length - availableSlots);
    const invalidCount = parsedEmails.invalid.length;

    const copy = isOnboarding
        ? {
              title: t('facilities.inviteUsersDialog.addTitle'),
              description: t('facilities.inviteUsersDialog.addDesc', { name: facilityName }),
              sendingLabel: t('facilities.inviteUsersDialog.adding'),
              sendLabel: (n: number) => t('facilities.inviteUsersDialog.addButton', { count: n }),
              successTitle: (n: number) => t('facilities.inviteUsersDialog.usersAdded', { count: n }),
          }
        : {
              title: t('facilities.inviteUsersDialog.inviteTitle'),
              description: t('facilities.inviteUsersDialog.inviteDesc', { name: facilityName }),
              sendingLabel: t('facilities.inviteUsersDialog.sending'),
              sendLabel: (n: number) => t('facilities.inviteUsersDialog.sendButton', { count: n }),
              successTitle: (n: number) => t('facilities.inviteUsersDialog.invitationsSent', { count: n }),
          };

    useEffect(() => {
        if (open) setPasteValue('');
    }, [open]);

    const handleSend = async () => {
        if (emails.length === 0) return;

        setSending(true);
        try {
            await sendBulkInvites(
                emails.map((email) => ({ email })),
                departmentId
            );
            toast({ title: copy.successTitle(emails.length) });
            onOpenChange(false);
            onInvited();
        } catch {
            toast({
                title: t('facilities.inviteUsersDialog.error'),
                description: t('facilities.inviteUsersDialog.sendError'),
                variant: 'destructive',
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{copy.title}</DialogTitle>
                    <DialogDescription>
                        {copy.description}. {t('facilities.inviteUsersDialog.separateHint')}
                    </DialogDescription>
                </DialogHeader>

                {overflowCount > 0 && (
                    <div className="rounded-md border border-rose-400/20 bg-rose-400/5 px-3 py-2 text-xs text-rose-200">
                        {t('facilities.inviteUsersDialog.overflowNote', { count: overflowCount })}
                    </div>
                )}

                {invalidCount > 0 && (
                    <div className="rounded-md border border-rose-400/20 bg-rose-400/5 px-3 py-2 text-xs text-rose-200">
                        {t('onboarding.users.toasts.invalidEmails', { count: invalidCount })}
                    </div>
                )}

                <div className="space-y-4 py-2">
                    <textarea
                        className="w-full rounded-md border border-white/10 bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-lime-500/50 focus:outline-none focus:ring-1 focus:ring-lime-500/30"
                        rows={6}
                        placeholder={t('facilities.inviteUsersDialog.placeholder')}
                        value={pasteValue}
                        onChange={(e) => setPasteValue(e.target.value)}
                    />

                    {emails.length > 0 && (
                        <div className="rounded-md border border-white/10 bg-white/5 p-3">
                            <p className="mb-2 text-xs text-muted-foreground">
                                {t('facilities.inviteUsersDialog.emailsDetected', { count: emails.length })}
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                                {emails.map((email) => (
                                    <span
                                        key={email}
                                        className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-xs text-foreground"
                                    >
                                        {email}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end gap-2">
                        <Button
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                        >
                            {t('facilities.inviteUsersDialog.cancel')}
                        </Button>
                        <Button
                            onClick={handleSend}
                            disabled={emails.length === 0 || sending}
                        >
                            <Send
                                className="mr-1.5 h-3.5 w-3.5"
                                aria-hidden="true"
                            />
                            {sending
                                ? copy.sendingLabel
                                : copy.sendLabel(emails.length)}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
